import { useRef, useState, useEffect } from 'react'
import { Dropdown, Typography, App, Empty, Input, Modal } from 'antd'
import { DismissibleTooltip } from '../common/DismissibleTooltip'
import {
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  PlusOutlined,
  InboxOutlined,
  CopyOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { useMindmaps } from '../../hooks/useMindmaps'
import type { MindMapMetadata } from '@bandos/shared'
import {
  StyledCard,
  CardHeader,
  TitleContainer,
  CardTitle,
  UpdatedText,
  MoreIconButton,
  SearchEmptyContainer,
  EmptyContainer,
  EmptyDescription,
  EmptyTitle,
  EmptySubtitle,
  CreateProjectButton,
  MindmapGrid,
} from './styles/MindmapTableStyles'
import { SkeletonProjectGrid } from '../../features/skeleton'

const { Text } = Typography

interface MindmapCardProps {
  mindmap: MindMapMetadata
  onEdit: (mindmap: MindMapMetadata) => void
  onDuplicate: (mindmap: MindMapMetadata) => void
  onDelete: (mindmap: MindMapMetadata) => void
  onArchive: (mindmap: MindMapMetadata) => void
  onClick: () => void
}

function MindmapCard({
  mindmap,
  onEdit,
  onDuplicate,
  onDelete,
  onArchive,
  onClick,
}: MindmapCardProps) {
  const titleRef = useRef<HTMLElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        setIsTruncated(titleRef.current.scrollWidth > titleRef.current.clientWidth)
      }
    }
    checkTruncation()
    window.addEventListener('resize', checkTruncation)
    return () => window.removeEventListener('resize', checkTruncation)
  }, [mindmap.title])

  return (
    <StyledCard hoverable onClick={onClick}>
      <CardHeader>
        <TitleContainer>
          <DismissibleTooltip title={isTruncated ? mindmap.title : undefined}>
            <CardTitle ref={titleRef} level={5}>
              {mindmap.title}
            </CardTitle>
          </DismissibleTooltip>
          <UpdatedText>
            Edited {formatDistanceToNow(new Date(mindmap.updated_at), { addSuffix: false })} ago
          </UpdatedText>
        </TitleContainer>
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Rename',
                icon: <EditOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation()
                  onEdit(mindmap)
                },
              },
              {
                key: 'duplicate',
                label: 'Duplicate',
                icon: <CopyOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation()
                  onDuplicate(mindmap)
                },
              },
              {
                key: 'archive',
                label: mindmap.is_archived ? 'Unarchive' : 'Archive',
                icon: <InboxOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation()
                  onArchive(mindmap)
                },
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation()
                  onDelete(mindmap)
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <MoreIconButton
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      </CardHeader>
    </StyledCard>
  )
}

interface MindmapGridProps {
  mindmaps: MindMapMetadata[]
  loading: boolean
  searchText: string
  loadMindmaps: () => void
  onCreateProject: () => void
  isArchivedView: boolean
}

export function MindmapTable({
  mindmaps,
  loading,
  searchText,
  loadMindmaps,
  onCreateProject,
  isArchivedView,
}: MindmapGridProps) {
  const navigate = useNavigate()
  const { modal, notification } = App.useApp()
  const { deleteMindmap, updateMindmap, duplicateMindmap } = useMindmaps()
  const [editingMindmap, setEditingMindmap] = useState<MindMapMetadata | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [saving, setSaving] = useState(false)

  const filteredMindmaps = mindmaps
    .filter((mindmap) => mindmap.title.toLowerCase().includes(searchText.toLowerCase()))
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())

  const handleEdit = (mindmap: MindMapMetadata) => {
    setEditingMindmap(mindmap)
    setEditTitle(mindmap.title)
  }

  const handleSaveEdit = async () => {
    if (!editingMindmap || !editTitle.trim()) return

    setSaving(true)
    const { error } = await updateMindmap(editingMindmap.id, { title: editTitle.trim() })
    setSaving(false)

    if (error) {
      notification.error({
        title: 'Failed to update title',
        description: error.message,
      })
    } else {
      notification.success({
        title: 'Title updated',
      })
      setEditingMindmap(null)
      loadMindmaps()
    }
  }

  const handleDuplicate = async (mindmap: MindMapMetadata) => {
    const { error } = await duplicateMindmap(mindmap.id)
    if (error) {
      notification.error({
        title: 'Failed to duplicate project',
        description: error.message,
      })
    } else {
      notification.success({
        title: 'Project duplicated',
      })
      loadMindmaps()
    }
  }

  const handleDelete = (mindmap: MindMapMetadata) => {
    modal.confirm({
      title: 'Delete project',
      icon: null,
      content: `Are you sure you want to delete "${mindmap.title}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const { error } = await deleteMindmap(mindmap.id)
        if (error) {
          notification.error({
            title: 'Failed to delete mindmap',
            description: error.message,
          })
        } else {
          notification.success({
            title: 'Mindmap deleted successfully',
          })
          loadMindmaps()
        }
      },
    })
  }

  const handleArchive = async (mindmap: MindMapMetadata) => {
    const newArchivedState = !mindmap.is_archived
    const { error } = await updateMindmap(mindmap.id, { is_archived: newArchivedState })
    if (error) {
      notification.error({
        title: `Failed to ${newArchivedState ? 'archive' : 'unarchive'} project`,
        description: error.message,
      })
    } else {
      notification.success({
        title: newArchivedState ? 'Project archived' : 'Project unarchived',
      })
      loadMindmaps()
    }
  }

  if (loading) {
    return <SkeletonProjectGrid />
  }

  if (filteredMindmaps.length === 0) {
    if (searchText) {
      return (
        <SearchEmptyContainer>
          <Text type="secondary">No projects found matching "{searchText}"</Text>
        </SearchEmptyContainer>
      )
    }

    if (isArchivedView) {
      return (
        <EmptyContainer>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <EmptyDescription>
                <EmptyTitle>No archived projects</EmptyTitle>
                <EmptySubtitle type="secondary">
                  Projects you archive will appear here
                </EmptySubtitle>
              </EmptyDescription>
            }
          />
        </EmptyContainer>
      )
    }

    return (
      <EmptyContainer>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <EmptyDescription>
              <EmptyTitle>No projects yet</EmptyTitle>
              <EmptySubtitle type="secondary">
                Create your first one to start ideating with Bandos!
              </EmptySubtitle>
            </EmptyDescription>
          }
        >
          <CreateProjectButton
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={onCreateProject}
          >
            Create your first project
          </CreateProjectButton>
        </Empty>
      </EmptyContainer>
    )
  }

  return (
    <>
      <MindmapGrid className="mindmap-grid">
        {filteredMindmaps.map((mindmap) => (
          <MindmapCard
            key={mindmap.id}
            mindmap={mindmap}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onArchive={handleArchive}
            onClick={() => navigate(`/mindmap/${mindmap.id}`)}
          />
        ))}
      </MindmapGrid>

      <Modal
        title="Rename project"
        open={!!editingMindmap}
        onOk={handleSaveEdit}
        onCancel={() => setEditingMindmap(null)}
        okText="Save"
        okButtonProps={{ loading: saving, disabled: !editTitle.trim() }}
      >
        <Input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          placeholder="Enter title"
          onPressEnter={handleSaveEdit}
          autoFocus
        />
      </Modal>
    </>
  )
}
